import { useTranslation } from 'react-i18next';
import { BellRing, CirclePlus } from 'lucide-react';

import { EmptyState, RelativeTime, SectionCard } from '../../../components/design-system/admin';
import { PROVIDER_NAMES, type CostEvent } from '../../../common/models/platform-costs.model';
import { money } from './cost-format';

/** Money added (read off the balance history) and the alerts sent, newest first. */
export function CostEventsCard({ events, className }: { events: CostEvent[]; className?: string }) {
  const { t } = useTranslation();
  const name = (id: string) => PROVIDER_NAMES[id as keyof typeof PROVIDER_NAMES] || (id === 'all' ? '∑' : id);

  return (
    <SectionCard title={t('ADMIN_COSTS.EVENTS.TITLE')} description={t('ADMIN_COSTS.EVENTS.DESCRIPTION')} className={className} testId="costs-events">
      {!events.length ? (
        <EmptyState title={t('ADMIN_COSTS.EVENTS.NONE')} className="py-8" />
      ) : (
        <ol className="flex flex-col gap-3">
          {events.slice(0, 12).map((e, i) => (
            <li key={`${e.at}-${i}`} className="flex items-start gap-2.5 text-sm">
              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md border bg-muted/40 text-muted-foreground [&_svg]:size-3.5">
                {e.kind === 'topup' ? <CirclePlus /> : <BellRing />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block">
                  {e.kind === 'topup'
                    ? t('ADMIN_COSTS.EVENTS.TOPUP', { provider: name(e.provider), amount: money(e.amountUsd) })
                    : t(`ADMIN_COSTS.EVENTS.ALERT_${e.type}`, { provider: name(e.provider), defaultValue: e.type })}
                </span>
                <RelativeTime value={e.at} className="text-[11px] text-muted-foreground" />
              </span>
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  );
}
